import '../participation.css';

export const CONTRIBUTION_CATEGORIES = [
  'Agua, drenaje e infraestructura',
  'Movilidad y transporte',
  'Espacio público y accesibilidad',
  'Medio ambiente, humedales y riesgos',
  'Vivienda, suelo y crecimiento urbano',
  'Patrimonio, cultura e identidad',
  'Seguridad y convivencia',
  'Economía local, turismo y comercio',
  'Residuos y limpieza urbana',
  'Otro',
] as const;

export type ContributionCategory = (typeof CONTRIBUTION_CATEGORIES)[number];

export type GuidedAnswer = string;

interface QuestionDef {
  q: string;
  opts: string[];
}

export const GUIDED_QUESTIONS_MAP: Record<string, QuestionDef[]> = {
  'Agua, drenaje e infraestructura': [
    {
      q: '¿Qué problema observas?',
      opts: ['Falta de agua potable', 'Drenaje insuficiente o colapsado', 'Tuberías rotas o fugas', 'Falta de alumbrado público'],
    },
    {
      q: '¿Qué tan urgente es?',
      opts: ['Urgente, afecta a diario', 'Importante pero no urgente', 'Para el mediano plazo', 'Para planificar a futuro'],
    },
    {
      q: '¿Qué mejora priorizarías?',
      opts: ['Ampliar red de agua potable', 'Mejorar drenaje pluvial y sanitario', 'Mantenimiento de infraestructura existente', 'Mejorar alumbrado público'],
    },
  ],
  'Movilidad y transporte': [
    {
      q: '¿Qué problema de movilidad observas aquí?',
      opts: ['Congestionamiento vial', 'Transporte público insuficiente', 'Falta de banquetas o cruces seguros', 'Falta de infraestructura ciclista'],
    },
    {
      q: '¿Qué tan urgente es?',
      opts: ['Urgente, afecta a diario', 'Importante pero no urgente', 'Para el mediano plazo', 'Para planificar a futuro'],
    },
    {
      q: '¿Qué mejora priorizarías?',
      opts: ['Mejorar transporte público', 'Mejorar banquetas y cruces peatonales', 'Ordenar tránsito y estacionamiento', 'Infraestructura ciclista'],
    },
  ],
  'Espacio público y accesibilidad': [
    {
      q: '¿Qué problema identificas en el espacio público?',
      opts: ['Falta de vegetación y sombra', 'Iluminación insuficiente', 'Sin mobiliario ni bancas', 'Barreras para personas con discapacidad'],
    },
    {
      q: '¿Qué tan urgente es?',
      opts: ['Urgente, afecta a diario', 'Importante pero no urgente', 'Para el mediano plazo', 'Para planificar a futuro'],
    },
    {
      q: '¿Qué mejora priorizarías?',
      opts: ['Recuperación de parques y plazas', 'Mejor iluminación pública', 'Accesibilidad universal', 'Mobiliario y arbolado urbano'],
    },
  ],
  'Medio ambiente, humedales y riesgos': [
    {
      q: '¿Qué problema ambiental o de riesgo observas?',
      opts: ['Inundaciones recurrentes', 'Afectación de humedales o zonas verdes', 'Deslaves o erosión en laderas', 'Contaminación de agua o suelo'],
    },
    {
      q: '¿Con qué frecuencia ocurre?',
      opts: ['En cada temporada de lluvias', 'Algunas veces al año', 'Rara vez', 'Es un problema crónico y constante'],
    },
    {
      q: '¿Qué acción priorizarías?',
      opts: ['Obras de drenaje pluvial', 'Protección y recuperación de humedales', 'Reforestación y protección de laderas', 'Sistemas de alerta y prevención'],
    },
  ],
  'Vivienda, suelo y crecimiento urbano': [
    {
      q: '¿Qué problema relacionado a vivienda o crecimiento observas?',
      opts: ['Crecimiento desordenado sin servicios', 'Precios de vivienda inaccesibles', 'Ocupación en zonas de riesgo', 'Pérdida de suelo verde o productivo'],
    },
    {
      q: '¿Qué tan urgente es?',
      opts: ['Urgente, afecta a diario', 'Importante pero no urgente', 'Para el mediano plazo', 'Para planificar a futuro'],
    },
    {
      q: '¿Qué tipo de intervención priorizarías?',
      opts: ['Regularización de asentamientos', 'Dotación de servicios básicos', 'Vivienda social bien ubicada', 'Mejor regulación del crecimiento'],
    },
  ],
  'Patrimonio, cultura e identidad': [
    {
      q: '¿Cómo evalúas el estado del patrimonio en esta zona?',
      opts: ['Bien preservado', 'Con algunas amenazas', 'En deterioro', 'En riesgo crítico'],
    },
    {
      q: '¿Qué tipo de patrimonio reconoces aquí?',
      opts: ['Arquitectura histórica o colonial', 'Tradiciones, festividades y artesanías', 'Paisaje natural y cultural', 'Espacios comunitarios activos'],
    },
    {
      q: '¿Qué acción priorizarías?',
      opts: ['Restauración de edificios históricos', 'Apoyo a tradiciones y festividades', 'Turismo cultural responsable', 'Educación y difusión comunitaria'],
    },
  ],
  'Seguridad y convivencia': [
    {
      q: '¿Cómo percibes la seguridad en esta zona?',
      opts: ['Muy segura', 'Medianamente segura', 'Poco segura', 'Insegura'],
    },
    {
      q: '¿En qué momentos se siente menos segura?',
      opts: ['De noche', 'Al anochecer', 'En espacios sin iluminación', 'En cualquier horario'],
    },
    {
      q: '¿Qué mejora priorizarías?',
      opts: ['Más iluminación pública', 'Mayor presencia y organización vecinal', 'Mejores espacios públicos', 'Programas de convivencia comunitaria'],
    },
  ],
  'Economía local, turismo y comercio': [
    {
      q: '¿Cómo está la actividad económica en tu zona?',
      opts: ['Activa y diversa', 'Concentrada en pocos negocios', 'Estacional o irregular', 'Débil o en caída'],
    },
    {
      q: '¿El turismo impacta a tu zona?',
      opts: ['Sí, genera ingresos y oportunidades', 'Sí, pero genera problemas (ruido, precios)', 'Poco o nada', 'No hay turismo aquí'],
    },
    {
      q: '¿Qué tipo de actividad económica promoverías?',
      opts: ['Comercio local y artesanía', 'Turismo cultural responsable', 'Gastronomía e identidad local', 'Nuevos emprendimientos y servicios'],
    },
  ],
  'Residuos y limpieza urbana': [
    {
      q: '¿Qué problema de residuos o limpieza observas aquí?',
      opts: ['Basura en calles o lotes baldíos', 'Tiraderos clandestinos', 'Recolección insuficiente o irregular', 'Falta de botes o contenedores'],
    },
    {
      q: '¿Con qué frecuencia ocurre?',
      opts: ['Es un problema constante', 'En temporadas específicas', 'Rara vez', 'Solo en algunos puntos'],
    },
    {
      q: '¿Qué mejora priorizarías?',
      opts: ['Mejores horarios de recolección', 'Más botes y contenedores en la vía pública', 'Campañas de educación ambiental', 'Limpieza de tiraderos y lotes'],
    },
  ],
  'Otro': [
    {
      q: '¿Sobre qué tema quieres aportar?',
      opts: ['Sobre la ciudad en general', 'Sobre un servicio específico', 'Sobre un problema puntual', 'Sobre una propuesta nueva'],
    },
    {
      q: '¿Con qué urgencia necesita atención?',
      opts: ['Urgente, ya', 'En el mediano plazo', 'Para planificar a futuro', 'Es una mejora deseable'],
    },
    {
      q: '¿Quiénes se beneficiarían más?',
      opts: ['Toda la comunidad', 'Personas mayores o con movilidad reducida', 'Niños y jóvenes', 'Comerciantes y trabajadores'],
    },
  ],
};

export function buildGuidedComment(
  category: string,
  answers: GuidedAnswer[],
  additionalComment: string,
): string {
  const questions = GUIDED_QUESTIONS_MAP[category];
  if (!questions) return additionalComment;
  const lines: string[] = [`[${category}]`];
  questions.forEach((def, i) => {
    if (answers[i]) lines.push(`${i + 1}. ${def.q} → ${answers[i]}`);
  });
  if (additionalComment.trim()) lines.push(`Comentario adicional: ${additionalComment.trim()}`);
  return lines.join('\n');
}

interface GuidedQuestionsProps {
  category: string;
  answers: GuidedAnswer[];
  onChange: (answers: GuidedAnswer[]) => void;
}

export function GuidedQuestions({ category, answers, onChange }: GuidedQuestionsProps): JSX.Element {
  const questions = GUIDED_QUESTIONS_MAP[category] ?? GUIDED_QUESTIONS_MAP['Otro'];

  function toggle(questionIdx: number, opt: string): void {
    const next = [...answers];
    next[questionIdx] = next[questionIdx] === opt ? '' : opt;
    onChange(next);
  }

  return (
    <div>
      {questions.map((def, qi) => (
        <div key={qi} className="guided-question">
          <p className="guided-question-label">{def.q}</p>
          <div className="guided-options">
            {def.opts.map(opt => (
              <button
                key={opt}
                type="button"
                className={`guided-option${answers[qi] === opt ? ' selected' : ''}`}
                onClick={() => toggle(qi, opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
